import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { Chip, Input, ShellHeader } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';

import './MemoryPolicyPage.scss';
import { Trash2 as DeleteForeverIcon, Gavel as GavelIcon, Lock as LockIcon, SlidersHorizontal as TuneIcon } from 'lucide-react';
import { memoryModuleService } from '../services/memoryModuleService';
import type { TenantMemoryPolicyDto } from '../types/memoryModule';
import { useContextStore } from '../store/contextStore';

const BoolChip = ({ value, onLabel = 'Enforced', offLabel = 'Off' }: {
  value: boolean;
  onLabel?: string;
  offLabel?: string;
}) => (
  <Chip variant={value ? 'filled' : 'outlined'} label={value ? onLabel : offLabel} />
);

const PolicyRow = ({ label, help, children }: {
  label: string;
  help: string;
  children: React.ReactNode;
}) => (
  <div className="flex justify-between items-start py-2 gap-4">
    <div>
      <p className="text-sm font-semibold">{label}</p>
      <span className="text-xs text-[var(--color-text-secondary)]">{help}</span>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const Section = ({ icon, title, children, 'data-testid': dataTestId }: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  'data-testid'?: string;
}) => (
  <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4" data-testid={dataTestId}>
    <div className="flex items-center gap-1.5 mb-2">
      {icon}
      <h6 className="text-base font-semibold">{title}</h6>
    </div>
    <div className="[&>div+div]:border-t [&>div+div]:border-[var(--glass-border)]">{children}</div>
  </div>
);

/**
 * Tenant Memory Policy — a read-only invariants view (Memory spec §2A.3). Policy changes go
 * through the compliance-owned versioning process, never through this UI; AMD settings are
 * clamped to these bounds at resolution time.
 */
export const MemoryPolicyPage = () => {
  const isGlobalAccess = useContextStore((s) => s.isGlobalAccess);

  const [policy, setPolicy] = useState<TenantMemoryPolicyDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Erasure panel (Admin only, §7.3.1)
  const [subjectId, setSubjectId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const runErasure = async () => {
    setErasing(true);
    setError(null);
    try {
      const counts = await memoryModuleService.eraseSubject(subjectId.trim());
      setToast(
        `Erasure complete: ${counts.candidatesErased} candidates, ${counts.outboxRowsDeleted} queue rows, ` +
        `${counts.recordsRetracted} records retracted, ${counts.episodesUnlinked} episodes unlinked, ` +
        `${counts.fieldValuesUnlinked} field values unlinked, ${counts.stubsDeleted} stubs deleted. Injection audits retained.`
      );
      setSubjectId('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erasure failed');
    } finally {
      setErasing(false);
      setConfirmOpen(false);
    }
  };

  useEffect(() => {
    memoryModuleService
      .getPolicy()
      .then(setPolicy)
      .catch((err: any) => setError(err.response?.data?.message || 'Failed to load the tenant memory policy'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="memory-policy" data-testid="memory-policy-form-page">
      <ShellHeader
        title="Memory Policy"
        data-testid="memory-policy-header"
        description="The tenant-level invariants every Agent Memory Definition is clamped to. This view is read-only — policy changes are compliance-owned and arrive as new policy versions."
      />

      <PageContainer variant="narrow">
        {error && <InlineErrorPanel message={error} data-testid="memory-policy-error-panel" />}

      {loading && <LoadingSkeleton rows={6} data-testid="memory-policy-loading" />}

      {policy && (
        <>
          <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-3 mb-4 flex gap-4 flex-wrap">
            <p className="text-sm">
              <strong>Policy version:</strong> v{policy.policyVersion}
            </p>
            <p className="text-sm">
              <strong>Effective from:</strong> {new Date(policy.effectiveFromUtc).toLocaleString()}
            </p>
            <p className="text-sm">
              <strong>Approved by:</strong> {policy.approvedBy}
            </p>
            <p className="text-sm">
              <strong>Change reason:</strong> {policy.changeReason}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Section icon={<LockIcon className="text-blue-500" />} title="Isolation and safety" data-testid="memory-policy-section-isolation">
                <PolicyRow
                  label="Tenant isolation"
                  help="No memory ever crosses a tenant boundary"
                >
                  <BoolChip value={policy.tenantIsolationEnforced} />
                </PolicyRow>
                <PolicyRow
                  label="Agent memory isolation"
                  help="Memories are private to their Agent Memory Definition"
                >
                  <BoolChip value={policy.agentMemoryIsolationEnforced} />
                </PolicyRow>
                <PolicyRow
                  label="Anonymizer"
                  help="Episodes are anonymized before storage (Phase 3 write path)"
                >
                  <BoolChip value={policy.anonymizerEnabled} onLabel="Enabled" offLabel="Disabled" />
                </PolicyRow>
                <PolicyRow
                  label="Procedural rules require human approval"
                  help="Every rule version passes the safety-gate queue before it can inject"
                >
                  <BoolChip value={policy.proceduralRequiresHumanApproval} onLabel="Required" offLabel="Not required" />
                </PolicyRow>
              </Section>
            </div>

            <div>
              <Section icon={<TuneIcon className="text-blue-500" />} title="Budget ceilings" data-testid="memory-policy-section-budgets">
                <PolicyRow
                  label="Max instructions token budget"
                  help="AMD instructions budgets are clamped to this ceiling"
                >
                  <p className="text-sm font-semibold">
                    {policy.maxInstructionsTokenBudget.toLocaleString()}
                  </p>
                </PolicyRow>
                <PolicyRow
                  label="Max episodic token budget"
                  help="AMD episodic budgets are clamped to this ceiling"
                >
                  <p className="text-sm font-semibold">
                    {policy.maxEpisodicTokenBudget.toLocaleString()}
                  </p>
                </PolicyRow>
                <PolicyRow
                  label="Max negative episode fraction"
                  help="Upper bound on the share of failure episodes in an injection"
                >
                  <p className="text-sm font-semibold">
                    {policy.maxNegativeEpisodeFraction}
                  </p>
                </PolicyRow>
              </Section>
            </div>

            <div>
              <Section icon={<GavelIcon className="text-blue-500" />} title="Evaluation and reflection" data-testid="memory-policy-section-evaluation">
                <PolicyRow
                  label="Sampling floor"
                  help="Bounds on per-rule sampling for efficacy measurement"
                >
                  <p className="text-sm font-semibold">
                    {policy.minSamplingFloor} – {policy.maxSamplingFloor}
                  </p>
                </PolicyRow>
                <PolicyRow
                  label="Max eval holdout rate"
                  help="Ceiling on the share of sessions withheld from memory for evaluation"
                >
                  <p className="text-sm font-semibold">
                    {policy.maxEvalHoldoutRate}
                  </p>
                </PolicyRow>
                <PolicyRow
                  label="Reflection"
                  help="Whether LLM-proposed rules (Phase 5) are permitted at all for this tenant"
                >
                  <BoolChip value={policy.reflectionAllowed} onLabel="Allowed" offLabel="Blocked" />
                </PolicyRow>
              </Section>
            </div>

            {isGlobalAccess && (
              <div>
                <Section icon={<DeleteForeverIcon className="text-red-500" />} title="Subject erasure" data-testid="memory-policy-section-erasure">
                  <p className="text-sm text-[var(--color-text-secondary)] mb-3">
                    Erases one subject across the memory stores: candidates (including quarantined),
                    the extraction queue, memory records (retracted, provenance cleared) and rejected-
                    session stubs. Injection audits are retained by design. The identifier is used for
                    matching and never echoed back — the result is counts only.
                  </p>
                  <div className="flex gap-2 items-start">
                    <div className="w-full">
                      <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Subject identifier (member id, user id)</label>
                      <Input
                        className="w-full"
                        value={subjectId}
                        onChange={(e) => setSubjectId(e.target.value)}
                        data-testid="memory-policy-subjectId"
                      />
                    </div>
                    <button
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      disabled={!subjectId.trim() || erasing}
                      onClick={() => setConfirmOpen(true)}
                      data-testid="memory-policy-erase-btn"
                    >
                      Erase
                    </button>
                  </div>
                </Section>
              </div>
            )}
          </div>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => !erasing && setConfirmOpen(false)} data-testid="memory-policy-confirm-dialog">
        <DialogTitle>Erase this subject from memory?</DialogTitle>
        <DialogContent>
          <p className="text-sm">
            This retracts every memory record, discards every candidate and deletes every queue row
            and stub tied to the identifier. It cannot be undone. Injection audits are kept.
          </p>
        </DialogContent>
        <DialogActions>
          <button className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-subtle)] disabled:opacity-50" onClick={() => setConfirmOpen(false)} disabled={erasing} data-testid="cancel-memory-policy-btn">Cancel</button>
          <button className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50" onClick={runErasure} disabled={erasing} data-testid="memory-policy-confirm-erase-btn">
            {erasing ? 'Erasing…' : 'Erase permanently'}
          </button>
        </DialogActions>
      </Dialog>

        <Snackbar
          open={toast !== null}
          autoHideDuration={8000}
          onClose={() => setToast(null)}
          message={toast}
        />
      </PageContainer>
    </div>
  );
};

export default MemoryPolicyPage;
